import React, { useState, useEffect, useRef } from "react"

import styled from "styled-components"
import Typed from "typed.js"
import { marked } from "marked"
import moment from "moment"
import { IFsNodeSchema } from "@cloudike/web_files"

import { MessageAttachments } from "./MessageAttachments"

interface AiMessageProps {
  message: string
  animated?: boolean
  messageId?: string
  onAnimationEnd?: () => void
  created?: string
  attachments?: IFsNodeSchema[]
}

export const AiMessage = React.memo(({ message, animated = false, messageId, created, onAnimationEnd, attachments }: AiMessageProps) => {
  const [htmlContent, setHtmlContent] = useState("")
  const [isAnimationCompleted, setIsAnimationCompleted] = useState(false)
  const typedRef = useRef<HTMLDivElement>(null)
  const typedInstance = useRef<Typed | null>(null)

  useEffect(() => {
    const processMarkdown = async () => {
      const html = await marked(message)
      setHtmlContent(html)
    }

    processMarkdown()
  }, [message])

  useEffect(() => {
    setIsAnimationCompleted(false)
  }, [messageId, animated])

  useEffect(() => {
    if (animated && htmlContent && typedRef.current && !isAnimationCompleted) {
      if (typedInstance.current) {
        typedInstance.current.destroy()
      }

      typedInstance.current = new Typed(typedRef.current, {
        strings: [htmlContent],
        typeSpeed: 2,
        backSpeed: 1,
        loop: false,
        showCursor: false,
        contentType: 'html',
        onComplete: () => {
          setIsAnimationCompleted(true)
          onAnimationEnd?.()
        },
      })
    }
  }, [animated, htmlContent, messageId, isAnimationCompleted])

  return (
    <SMessageContainer>
      <SMessage>
        {animated && htmlContent && !isAnimationCompleted ? (
          <SMessageContent ref={typedRef} />
        ) :
          (
            <SMessageContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}

        {attachments && attachments.length > 0 && (
          <SAttachmentsWrapper>
            <MessageAttachments attachments={attachments}
              type="ai-assistant-generated"
            />
          </SAttachmentsWrapper>
        )}
      </SMessage>

      <STimestamp>
        {moment(created).fromNow()}
      </STimestamp>
    </SMessageContainer>
  )
})

const SMessageContainer = styled.div`
  padding-right: 56px;
  user-select: auto;
`

const SMessageContent = styled.div`
  user-select: auto;

  img {
    max-width: 100%;
    height: auto;
  }
`

const SMessage = styled.div`
  padding: 16px;
  border-radius: 16px;
  background-color: #F5F5F5;
  color: #000000E0;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  border-bottom-left-radius: 0;
  user-select: auto;

  h1, h2, h3, h4, h5, h6 {
    margin: 16px 0 8px 0;
    font-weight: 600;
  }

  h1 { font-size: 18px; }
  h2 { font-size: 16px; }
  h3 { font-size: 15px; }

  p {
    margin: 8px 0;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  code {
    background-color: #f0f0f0;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
  }

  pre {
    background-color: #f0f0f0;
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;
  }

  pre code {
    background: none;
    padding: 0;
  }

  blockquote {
    border-left: 4px solid #ddd;
    margin: 8px 0;
    padding-left: 16px;
    color: #666;
  }
`

const STimestamp = styled.div`
  margin-top: 10px;
  color: #00000073;
  font-size: 12px;
  line-height: 22px;
  font-weight: 400;
  display: flex;
  justify-content: flex-end;
`

const SAttachmentsWrapper = styled.div`
  margin-top: 16px;
`